import { Button } from '@/components/ui/Button';
import { Icon } from '@/components/ui/Icon';
import {
  mdiAlert,
  mdiAlertCircle,
  mdiCheckCircle,
  mdiClose,
  mdiInformation,
} from '@mdi/js';
import cx from 'classnames';
import * as React from 'react';
import { createPortal } from 'react-dom';

interface Toast {
  id: string;
  type: 'success' | 'error' | 'warning' | 'info';
  title: string;
  message: string;
  duration?: number;
  persistent?: boolean;
}

export interface ToastProps extends Toast {
  onClose: (id: string) => void;
}

interface ToastContainerProps {
  toasts: Toast[];
  onClose: (id: string) => void;
}

const ToastComponent: React.FC<ToastProps> = ({
  id,
  type,
  title,
  message,
  duration = 5000,
  persistent = false,
  onClose,
}) => {
  const [isVisible, setIsVisible] = React.useState(false);
  const [isLeaving, setIsLeaving] = React.useState(false);

  React.useEffect(() => {
    // Animate in
    const timer = setTimeout(() => setIsVisible(true), 50);
    return () => clearTimeout(timer);
  }, []);

  const handleClose = React.useCallback(() => {
    setIsLeaving(true);
    setTimeout(() => {
      onClose(id);
    }, 300);
  }, [id, onClose]);

  React.useEffect(() => {
    if (!persistent && duration > 0) {
      const timer = setTimeout(() => {
        handleClose();
      }, duration);
      return () => clearTimeout(timer);
    }
  }, [duration, persistent, handleClose]);

  const icons = {
    success: mdiCheckCircle,
    error: mdiAlertCircle,
    warning: mdiAlert,
    info: mdiInformation,
  };

  const colors = {
    success: {
      bg: 'bg-green-50',
      border: 'border-green-200',
      icon: 'text-green-400',
      title: 'text-green-800',
      message: 'text-green-700',
    },
    error: {
      bg: 'bg-red-50',
      border: 'border-red-200',
      icon: 'text-red-400',
      title: 'text-red-800',
      message: 'text-red-700',
    },
    warning: {
      bg: 'bg-yellow-50',
      border: 'border-yellow-200',
      icon: 'text-yellow-400',
      title: 'text-yellow-800',
      message: 'text-yellow-700',
    },
    info: {
      bg: 'bg-blue-50',
      border: 'border-blue-200',
      icon: 'text-blue-400',
      title: 'text-blue-800',
      message: 'text-blue-700',
    },
  };

  const colorSet = colors[type];

  return (
    <div
      className={cx(
        'max-w-sm w-full shadow-lg rounded-lg border pointer-events-auto transform transition-all duration-300 ease-in-out',
        colorSet.bg,
        colorSet.border,
        isLeaving
          ? 'translate-x-full opacity-0'
          : isVisible
            ? 'translate-x-0 opacity-100'
            : 'translate-x-full opacity-0',
      )}
    >
      <div className='p-4'>
        <div className='flex items-start'>
          <div className='flex-shrink-0'>
            <Icon path={icons[type]} size={20} className={colorSet.icon} />
          </div>
          <div className='ml-3 w-0 flex-1 pt-0.5'>
            <p className={cx('text-sm font-medium', colorSet.title)}>{title}</p>
            <p className={cx('mt-1 text-sm', colorSet.message)}>{message}</p>
          </div>
          <div className='ml-4 flex-shrink-0 flex'>
            <Button
              icon={mdiClose}
              onClick={() => handleClose()}
              className='inline-flex text-grey-400 hover:text-grey-500 focus:outline-none'
              color='void'
              small
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export const ToastContainer: React.FC<ToastContainerProps> = ({
  toasts,
  onClose,
}) => {
  if (toasts.length === 0) return null;

  const toastPortal = (
    <div
      aria-live='assertive'
      className='fixed inset-0 flex items-end justify-center px-4 py-6 pointer-events-none sm:p-6 sm:items-start sm:justify-end z-50'
    >
      <div className='w-full flex flex-col items-center space-y-4 sm:items-end'>
        {toasts.map(toast => (
          <ToastComponent key={toast.id} {...toast} onClose={onClose} />
        ))}
      </div>
    </div>
  );

  return typeof document !== 'undefined'
    ? createPortal(toastPortal, document.body)
    : null;
};
